/**
 * Single source of truth for firm facts.
 * Everything printed on the site (and in JSON-LD) reads from here so the
 * name, address and phone stay identical everywhere — which is exactly what
 * Google's local algorithm rewards.
 */

import erinPhoto from "@/assets/erin-wilkinson.webp.asset.json";
import michaelPhoto from "@/assets/michael-klein-photo-v6.jpg";


export const FIRM = {
  name: "Coastside Law",
  legalName: "Coastside Law",
  tagline:
    "Property, wills and business lawyers — Sunshine Coast, Moreton Bay and Wide Bay",
  street: "40 Theresa Street",
  suburb: "Golden Beach",
  state: "QLD",
  postcode: "4551",
  country: "AU",
  phone: "0488 340 853",
  phoneHref: "tel:+61488340853",
  email: "erin@coastsidelaw.com.au",
  emailHref: "mailto:erin@coastsidelaw.com.au",
  hours: "Monday to Friday, 8:30am – 5:00pm (after-hours appointments by arrangement)",
  membership: "Members of the Queensland Law Society",
  poBox: "PO Box 233",
  poBoxSuburb: "Golden Beach",
  poBoxState: "QLD",
  poBoxPostcode: "4551",
} as const;

/**
 * Both direct lines. FIRM.phone stays the Golden Beach line (Erin) so the
 * primary NAP matches the registered address; Michael's Peninsula line is
 * listed alongside it wherever we show contact details in full.
 */
export const CONTACT_NUMBERS = [
  {
    name: "Erin Wilkinson",
    office: "Golden Beach",
    phone: "0488 340 853",
    phoneHref: "tel:+61488340853",
  },
  {
    name: "Michael Klein",
    office: "Scarborough",
    phone: "0419 000 884",
    phoneHref: "tel:+61419000884",
  },
] as const;

/** Michael Klein's direct line (Scarborough / Moreton Bay pages). */
export const MICHAEL_PHONE = CONTACT_NUMBERS[1].phone;
export const MICHAEL_PHONE_HREF = CONTACT_NUMBERS[1].phoneHref;

export const FIRM_ADDRESS = `${FIRM.street}, ${FIRM.suburb} ${FIRM.state} ${FIRM.postcode}`;

/** Postal address for mail and correspondence. */
export const FIRM_POSTAL_ADDRESS = `${FIRM.poBox}, ${FIRM.poBoxSuburb} ${FIRM.poBoxState} ${FIRM.poBoxPostcode}`;

/** Second office on the Redcliffe Peninsula, for Moreton Bay clients. */
export const PENINSULA_OFFICE = {
  label: "Peninsula office",
  street: "15 Fortune Street",
  suburb: "Scarborough",
  state: "QLD",
  postcode: "4020",
  country: "AU",
  note: "By appointment",
} as const;

export const PENINSULA_ADDRESS = `${PENINSULA_OFFICE.street}, ${PENINSULA_OFFICE.suburb} ${PENINSULA_OFFICE.state} ${PENINSULA_OFFICE.postcode}`;

/** Fixed conveyancing fees, both confirmed and inclusive of GST. */
export const FEES = {
  sale: "$990",
  saleNote: "incl. GST",
  purchase: "$1,499",
  purchaseNote: "incl. GST",
  contractReview: "Free",
  contractReviewNote: "pre-signing contract review, no obligation",
} as const;

/**
 * Promise made on residential conveyancing: no professional fee is charged if the
 * contract ends under a standard condition. Third-party disbursements already
 * incurred are still payable at cost.
 */
export const NO_CHARGE_GUARANTEE = {
  title: "No Charge Guarantee",
  short:
    "If your contract is terminated under cooling off, finance or building and pest, you pay no professional fee.",
  long:
    "We review your contract before you sign at no charge. If you go ahead and the contract is later terminated under the cooling-off period, the finance condition or the building and pest condition, we do not charge you a professional fee — you only pay for any searches or third-party disbursements already ordered, at cost. When you buy or sell your next property, we act for you on our usual fixed fee.",
  points: [
    "Free contract review before you sign — sale or purchase",
    "Terminated in the cooling-off period? No professional fee",
    "Finance not approved? No professional fee",
    "Building and pest report unsatisfactory? No professional fee",
    "You pay only for searches and disbursements already ordered, at cost",
  ],
} as const;

export const PEOPLE = [
  {
    slug: "michael-klein",
    linkedin: "https://www.linkedin.com/in/michaelkleinlawyer/",
    name: "Michael Klein",
    role: "Legal Practice Director",
    admitted: 2003,
    photo: michaelPhoto,
    based: "Redcliffe, Moreton Bay",
    courts: [
      "High Court of Australia",
      "Supreme Court of Queensland",
    ],
    areas: [
      "Litigation",
      "Insurance",
      "Property law",
      "Wills and estates",
      "Commercial law",
    ],
    bio: "Michael has practised law since his admission in 2003 and is admitted to both the High Court of Australia and the Supreme Court of Queensland. Over two decades he has acted in litigation, insurance, property, commercial and estate matters, which means the advice you get on a contract or a will is shaped by having seen how those documents hold up when they are tested. That litigation background includes extensive personal injury and motor vehicle accident work, acting for both insurers and injured people, so he reads risk the way the other side will. For many years he was managing partner of GKS Law, and he still acts for clients and takes referrals from agents he has worked with across Redcliffe and the wider Moreton Bay region, where he lives. He leads Coastside Law as Legal Practice Director.",
  },
  {
    slug: "erin-wilkinson",
    linkedin: "https://www.linkedin.com/in/erinwilkinsonlaw/",
    name: "Erin Wilkinson",
    role: "Solicitor",
    admitted: 2025,
    photo: erinPhoto.url,
    based: "Golden Beach, Caloundra",
    courts: ["Supreme Court of Queensland"],
    areas: ["Conveyancing", "Property law", "Wills and estates"],
    bio: "Erin was admitted as a solicitor of the Supreme Court of Queensland in 2025. Before that she spent years as a highly experienced conveyancer across South East Queensland, settling residential transactions week in and week out. That grounding means she knows where deals actually come unstuck — finance dates, building and pest, body corporate records — and handles them before they become a problem. Erin lives locally in Golden Beach.",
  },
] as const;

export const PRACTICE_AREAS = [
  {
    to: "/conveyancing",
    title: "Conveyancing",
    price: "Sales from $990 incl. GST",
    blurb:
      "Buying or selling on the Sunshine Coast, at a fixed fee agreed before we start. Contract reviews, cooling-off, finance and building and pest, right through to settlement.",
  },
  {
    to: "/wills-and-estates",
    title: "Wills & Estates",
    price: "Wills from $660 incl. GST",
    blurb:
      "Wills, probate, letters of administration, executor duties and estate disputes — explained in plain English, without the jargon.",
  },
  {
    to: "/elder-law",
    title: "Elder Law",
    price: "Powers of attorney from $440 incl. GST",
    blurb:
      "Enduring powers of attorney, advance health directives, granny flat and family agreements, aged care and capacity questions.",
  },
  {
    to: "/business-lawyers",
    title: "Business Law",
    price: "Form 12 + lease review $880 incl. GST",
    blurb:
      "Buying and selling businesses, commercial conveyancing, retail shop leases and Form 12 advice, and independent legal advice for guarantors.",
  },
] as const;

export const SERVICE_AREAS = [
  "Golden Beach",
  "Caloundra",
  "Pelican Waters",
  "Kings Beach",
  "Moffat Beach",
  "Currimundi",
  "Little Mountain",
  "Wurtulla",
  "Bribie Island",
  "Redcliffe",
  "Scarborough",
  "Woody Point",
  "Margate",
  "Clontarf",
  "Rothwell",
  "Deception Bay",
  "North Lakes",
  "Burpengary",
  "Morayfield",
  "Caboolture",
  "Woodford",
  "Hervey Bay",
  "Maryborough",
  "Gympie",
  "the wider Sunshine Coast",
] as const;

/**
 * Brand entity node. Google ranks "coastside law" at ~11 because it has no
 * strong entity for the name; this ties the name, alternate spellings, both
 * offices and the people to one canonical URL.
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LegalService"],
  "@id": "https://coastsidelaw.com.au/#organization",
  name: FIRM.name,
  legalName: FIRM.legalName,
  alternateName: ["Coastside Law QLD", "Coastside Lawyers", "Coastside Law Golden Beach"],
  url: "https://coastsidelaw.com.au",
  // The firm's own entity page — the canonical account of who we are and the
  // wills, estates and probate work we do.
  mainEntityOfPage: "https://coastsidelaw.com.au/wills-and-probate-queensland",
  subjectOf: {
    "@type": "AboutPage",
    "@id": "https://coastsidelaw.com.au/wills-and-probate-queensland#aboutpage",
    url: "https://coastsidelaw.com.au/wills-and-probate-queensland",
    name: "Coastside Law — wills, estates and probate in Queensland",
  },
  logo: "https://coastsidelaw.com.au/icon-512.png",
  slogan: FIRM.tagline,
  description: `${FIRM.name} is a Queensland law firm with offices at ${FIRM.street}, ${FIRM.suburb} and ${PENINSULA_OFFICE.street}, ${PENINSULA_OFFICE.suburb}, offering fixed-fee conveyancing, wills and estates, probate and business law.`,
  email: FIRM.email,
  telephone: FIRM.phone,
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  sameAs: [
    "https://www.linkedin.com/company/143455959/",
    "https://www.instagram.com/coastsidelaw/",
    "https://www.facebook.com/profile.php?id=61586552011485",
    "https://maps.google.com/?cid=17645464718929886346",
  ],
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: FIRM.street,
      addressLocality: FIRM.suburb,
      addressRegion: FIRM.state,
      postalCode: FIRM.postcode,
      addressCountry: FIRM.country,
    },
    {
      "@type": "PostalAddress",
      streetAddress: PENINSULA_OFFICE.street,
      addressLocality: PENINSULA_OFFICE.suburb,
      addressRegion: PENINSULA_OFFICE.state,
      postalCode: PENINSULA_OFFICE.postcode,
      addressCountry: FIRM.country,
    },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: FIRM.phone,
      email: FIRM.email,
      areaServed: "AU",
      availableLanguage: "English",
    },
  ],
  areaServed: [
    "Golden Beach",
    "Caloundra",
    "Pelican Waters",
    "Baringa",
    "Currimundi",
    "Bribie Island",
    "Scarborough",
    "Redcliffe",
    "Margate",
    "North Lakes",
    "Kallangur",
    "Brisbane",
  ].map((name) => ({
    "@type": "Place",
    name,
    address: {
      "@type": "PostalAddress",
      addressLocality: name,
      addressRegion: "QLD",
      addressCountry: "AU",
    },
  })),
  knowsAbout: [
    "Fixed fee conveyancing Queensland",
    "Property settlements Queensland",
    "Wills and estate planning",
    "Enduring powers of attorney",
    "Probate and letters of administration",
    "Estate administration Queensland",
    "Elder law",
    "Business sales and purchases",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Coastside Law legal services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Conveyancing - sale of property" },
        priceCurrency: "AUD",
        price: "990",
        url: "https://coastsidelaw.com.au/conveyancing",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Conveyancing - purchase of property" },
        priceCurrency: "AUD",
        price: "1499",
        url: "https://coastsidelaw.com.au/conveyancing",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Simple will" },
        priceSpecification: { "@type": "PriceSpecification", minPrice: 660, priceCurrency: "AUD", valueAddedTaxIncluded: true },
        url: "https://coastsidelaw.com.au/wills-and-estates",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Will and enduring power of attorney pack" },
        priceCurrency: "AUD",
        price: "825",
        url: "https://coastsidelaw.com.au/wills-and-estates",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Couples wills and EPOA pack" },
        priceCurrency: "AUD",
        price: "1499",
        url: "https://coastsidelaw.com.au/wills-and-estates",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Probate and estate administration" },
        url: "https://coastsidelaw.com.au/probate",
      },
      // Suburb-level wills and probate services, so the entity is understood to
      // cover each locality by name rather than just "Queensland".
      ...[
        { suburb: "Golden Beach", wills: "/wills/golden-beach", probate: "/probate/golden-beach" },
        { suburb: "Caloundra", wills: "/wills/caloundra", probate: "/probate/caloundra" },
        { suburb: "Pelican Waters", wills: "/wills/pelican-waters", probate: "/probate/pelican-waters" },
        { suburb: "Scarborough", wills: "/scarborough-wills-and-estates", probate: "/probate/scarborough" },
      ].flatMap(({ suburb, wills, probate }) => [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: `Wills and enduring powers of attorney — ${suburb}`,
            areaServed: {
              "@type": "Place",
              name: suburb,
              address: {
                "@type": "PostalAddress",
                addressLocality: suburb,
                addressRegion: "QLD",
                addressCountry: "AU",
              },
            },
          },
          url: `https://coastsidelaw.com.au${wills}`,
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: `Probate and letters of administration — ${suburb}`,
            areaServed: {
              "@type": "Place",
              name: suburb,
              address: {
                "@type": "PostalAddress",
                addressLocality: suburb,
                addressRegion: "QLD",
                addressCountry: "AU",
              },
            },
          },
          url: `https://coastsidelaw.com.au${probate}`,
        },
      ]),
    ],
  },

  department: [
    {
      "@type": "LegalService",
      name: "Coastside Law - Golden Beach office",
      telephone: FIRM.phone,
      url: "https://coastsidelaw.com.au/conveyancing/golden-beach",
      address: {
        "@type": "PostalAddress",
        streetAddress: FIRM.street,
        addressLocality: FIRM.suburb,
        addressRegion: FIRM.state,
        postalCode: FIRM.postcode,
        addressCountry: FIRM.country,
      },
    },
    {
      "@type": "LegalService",
      name: "Coastside Law - Scarborough office",
      telephone: MICHAEL_PHONE,
      url: "https://coastsidelaw.com.au/scarborough",
      address: {
        "@type": "PostalAddress",
        streetAddress: PENINSULA_OFFICE.street,
        addressLocality: PENINSULA_OFFICE.suburb,
        addressRegion: PENINSULA_OFFICE.state,
        postalCode: PENINSULA_OFFICE.postcode,
        addressCountry: FIRM.country,
      },
    },
  ],
  employee: PEOPLE.map((p) => ({
    "@type": "Person",
    name: p.name,
    jobTitle: p.role,
    url: "https://coastsidelaw.com.au/our-people",
    sameAs: [p.linkedin],
  })),
};


/** Reusable LegalService JSON-LD node. */

export const legalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": "https://coastsidelaw.com.au/#legalservice",
  name: FIRM.name,
  url: "https://coastsidelaw.com.au/",
  image: "https://coastsidelaw.com.au/icon-512.png",
  logo: "https://coastsidelaw.com.au/icon-512.png",
  priceRange: "$$",
  geo: { "@type": "GeoCoordinates", latitude: -26.8175, longitude: 153.1289 },
  description:
    "Sunshine Coast law firm at Golden Beach offering fixed-fee conveyancing, wills and estates, and elder law.",
  telephone: FIRM.phone,
  email: FIRM.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  areaServed: SERVICE_AREAS.filter((a) => a !== "the wider Sunshine Coast").map((name) => ({
    "@type": "Place",
    name,
  })),
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "17:00",
    },
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  employee: PEOPLE.map((p) => ({
    "@type": "Person",
    name: p.name,
    jobTitle: p.role,
    image: `https://coastsidelaw.com.au${p.photo}`,
    url: `https://coastsidelaw.com.au/our-people`,
    sameAs: [p.linkedin],
    worksFor: { "@id": "https://coastsidelaw.com.au/#legalservice" },
  })),
};

export function faqSchema(items: ReadonlyArray<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/**
 * LocalBusiness (Attorney) node for the Theresa Street office.
 * Emitted on the contact page — the canonical "where we are" page — with a
 * stable @id so Google can tie it to the Google Business Profile listing.
 */
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Attorney"],
  "@id": "https://coastsidelaw.com.au/contact#office",
  image: "https://coastsidelaw.com.au/icon-512.png",
  logo: "https://coastsidelaw.com.au/icon-512.png",
  name: FIRM.name,
  legalName: FIRM.legalName,
  description: `${FIRM.name} is a law firm at ${FIRM.street}, ${FIRM.suburb}, offering fixed-fee conveyancing, wills and estates and elder law across Caloundra and the Sunshine Coast.`,
  url: "https://coastsidelaw.com.au/",
  sameAs: [
    "https://maps.google.com/?cid=17645464718929886346",
    "https://www.linkedin.com/company/143455959/",
    "https://www.instagram.com/coastsidelaw/",
    "https://www.facebook.com/profile.php?id=61586552011485",
  ],
  telephone: FIRM.phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: -26.8175, longitude: 153.1289 },
  hasMap: "https://maps.google.com/maps?cid=17645464718929886346",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "17:00",
    },
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  areaServed: SERVICE_AREAS.filter((a) => a !== "the wider Sunshine Coast").map((name) => ({
    "@type": "Place",
    name,
  })),
  knowsAbout: [
    "Residential conveyancing",
    "Property law",
    "Wills and estates",
    "Probate",
    "Enduring powers of attorney",
    "Elder law",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      name: "Fixed-fee residential sale conveyancing",
      priceCurrency: "AUD",
      price: FEES.sale.replace(/[$,]/g, ""),
      description: `${FEES.sale} ${FEES.saleNote}`,
    },
    {
      "@type": "Offer",
      name: "Fixed-fee residential purchase conveyancing",
      priceCurrency: "AUD",
      price: FEES.purchase.replace(/[$,]/g, ""),
      description: `${FEES.purchase} ${FEES.purchaseNote}`,
    },
  ],
};

/**
 * Second LocalBusiness node for the Redcliffe Peninsula office.
 * Emitted on Moreton Bay pages only, and linked to the Golden Beach node
 * via branchOf so the two locations are understood as one firm.
 */
export const peninsulaBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Attorney"],
  "@id": "https://coastsidelaw.com.au/moreton-bay#scarborough-office",
  image: "https://coastsidelaw.com.au/icon-512.png",
  name: `${FIRM.name} — Scarborough`,
  legalName: FIRM.legalName,
  description: `${FIRM.name}'s Redcliffe Peninsula office at ${PENINSULA_OFFICE.street}, ${PENINSULA_OFFICE.suburb}, offering fixed-fee conveyancing, wills and estates and elder law across Moreton Bay.`,
  url: "https://coastsidelaw.com.au/scarborough",
  branchOf: { "@id": "https://coastsidelaw.com.au/contact#office",
  image: "https://coastsidelaw.com.au/icon-512.png",
  logo: "https://coastsidelaw.com.au/icon-512.png", name: FIRM.name },
  telephone: CONTACT_NUMBERS[1].phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  address: {
    "@type": "PostalAddress",
    streetAddress: PENINSULA_OFFICE.street,
    addressLocality: PENINSULA_OFFICE.suburb,
    addressRegion: PENINSULA_OFFICE.state,
    postalCode: PENINSULA_OFFICE.postcode,
    addressCountry: PENINSULA_OFFICE.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: -27.2003, longitude: 153.1085 },
  hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${FIRM.name}, ${PENINSULA_ADDRESS}`,
  )}`,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "17:00",
    },
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  areaServed: [
    "Redcliffe",
    "Scarborough",
    "Woody Point",
    "Margate",
    "Clontarf",
    "Newport",
    "Kippa-Ring",
    "Rothwell",
    "Deception Bay",
    "North Lakes",
    "Burpengary",
    "Morayfield",
    "Caboolture",
    "Woodford",
  ].map((name) => ({ "@type": "Place", name })),
  knowsAbout: [
    "Residential conveyancing",
    "Property law",
    "Wills and estates",
    "Probate",
    "Enduring powers of attorney",
    "Elder law",
  ],
};

/**
 * Brisbane node for the /brisbane hub. Coastside Law has no Brisbane shopfront,
 * so this is a service-area business: the Scarborough office is the physical
 * address and Brisbane and its suburbs are declared as areaServed. Linked back
 * to the Golden Beach node via branchOf so the three nodes read as one firm.
 */
export const brisbaneBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Attorney"],
  "@id": "https://coastsidelaw.com.au/brisbane#brisbane",
  image: "https://coastsidelaw.com.au/icon-512.png",
  name: `${FIRM.name} — Brisbane`,
  legalName: FIRM.legalName,
  description: `${FIRM.name} acts for Brisbane clients on fixed-fee conveyancing, wills and estates, probate and business matters. Queensland-wide electronic settlement through PEXA, with the nearest office at ${PENINSULA_ADDRESS}.`,
  url: "https://coastsidelaw.com.au/brisbane",
  branchOf: { "@id": "https://coastsidelaw.com.au/contact#office",
  image: "https://coastsidelaw.com.au/icon-512.png",
  logo: "https://coastsidelaw.com.au/icon-512.png", name: FIRM.name },
  telephone: CONTACT_NUMBERS[1].phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  address: {
    "@type": "PostalAddress",
    streetAddress: PENINSULA_OFFICE.street,
    addressLocality: PENINSULA_OFFICE.suburb,
    addressRegion: PENINSULA_OFFICE.state,
    postalCode: PENINSULA_OFFICE.postcode,
    addressCountry: PENINSULA_OFFICE.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: -27.4698, longitude: 153.0251 },
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: -27.4698, longitude: 153.0251 },
    geoRadius: "40000",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "17:00",
    },
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  areaServed: [
    "Brisbane",
    "Brisbane CBD",
    "New Farm",
    "Ascot",
    "Hamilton",
    "Chermside",
    "Aspley",
    "Sandgate",
    "Nundah",
    "Carindale",
    "Indooroopilly",
    "Mount Gravatt",
    "Wynnum",
    "Ipswich",
    "Logan",
  ].map((name) => ({ "@type": "Place", name })),
  knowsAbout: [
    "Residential conveyancing",
    "Off the plan apartment contracts",
    "Body corporate and community titles",
    "Probate and letters of administration",
    "Wills and estates",
    "Enduring powers of attorney",
    "Business sales and commercial leasing",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      name: "Fixed-fee residential sale conveyancing",
      priceCurrency: "AUD",
      price: FEES.sale.replace(/[$,]/g, ""),
      description: `${FEES.sale} ${FEES.saleNote}`,
    },
    {
      "@type": "Offer",
      name: "Fixed-fee residential purchase conveyancing",
      priceCurrency: "AUD",
      price: FEES.purchase.replace(/[$,]/g, ""),
      description: `${FEES.purchase} ${FEES.purchaseNote}`,
    },
    {
      "@type": "Offer",
      name: "Grant of probate, Supreme Court of Queensland",
      priceCurrency: "AUD",
      price: "3300",
      description: "$3,300 incl GST fixed professional fee, plus court filing and advertising at cost",
    },
  ],
};
